import { CheckCircle2, ChevronDown, MessageSquare, User } from 'lucide-react';
import { useMemo, useState } from 'react';
import { z } from 'zod';

import { Badge } from '@/components/ui/badge';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';

import { BaseBadge } from './BaseBadge';
import { BaseToolBadgeProps } from './types';

const TaskUpdateSchema = z.object({
  id: z.string(),
  update: z.string(),
  update_type: z.string(),
  visibility: z.string(),
  user: z.string().nullable(),
  agent: z.string().nullable(),
  created_at: z.string(),
});

const ReadTaskResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  status: z.string(),
  canonical_status: z.string(),
  type: z.string(),
  canonical_type: z.string(),
  group: z.string(),
  canonical_group: z.string(),
  due_date: z.string().nullable(),
  assignee: z.string().nullable(),
  created_at: z.string(),
  modified_at: z.string(),
  updates: z.array(TaskUpdateSchema),
});

type TaskUpdateItemProps = {
  update: z.infer<typeof TaskUpdateSchema>;
};

const TaskUpdateItem = ({ update }: TaskUpdateItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const date = new Date(update.created_at);
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const author = update.user || update.agent || 'System';
  const isActivity = update.update_type === 'activity';

  const handleToggle = () => setIsExpanded(!isExpanded);

  return (
    <div
      role="button"
      tabIndex={0}
      className="group flex flex-col gap-1.5 rounded-md p-2.5 hover:bg-muted/30 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={handleToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        }
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1">
          {isActivity ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-blue-500 mt-0.5" />
          ) : (
            <MessageSquare className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
          )}
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-foreground">
                {author}
              </span>
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {isActivity ? 'Activity' : 'Comment'}
              </Badge>
            </div>
            <span className="text-[10px] text-muted-foreground">
              {formattedDate}
            </span>
          </div>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-200',
            isExpanded && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </div>
      <div
        className={cn(
          'grid transition-all duration-200 ease-in-out',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <p className="text-xs text-foreground py-1 pl-6 whitespace-pre-wrap">
            {update.update}
          </p>
        </div>
      </div>
    </div>
  );
};

type TaskDetailsProps = {
  task: z.infer<typeof ReadTaskResponseSchema>;
};

const TaskDetails = ({ task }: TaskDetailsProps) => {
  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="border-b border-border p-4 bg-muted/30">
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-foreground">{task.name}</h3>
          {task.description && (
            <p className="text-xs text-muted-foreground">{task.description}</p>
          )}
          <div className="flex flex-wrap gap-2 mt-2">
            <Badge variant="outline" className="text-[10px] px-2 py-0.5">
              {task.status}
            </Badge>
            <Badge variant="secondary" className="text-[10px] px-2 py-0.5">
              {task.group}
            </Badge>
            <Badge variant="secondary" className="text-[10px] px-2 py-0.5">
              {task.type}
            </Badge>
            {task.assignee && (
              <Badge
                variant="outline"
                className="text-[10px] px-2 py-0.5 flex items-center gap-1"
              >
                <User className="h-2.5 w-2.5" />
                {task.assignee}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Updates */}
      <div className="max-h-[350px] overflow-y-auto scrollbar">
        {task.updates.length > 0 ? (
          <div className="divide-y divide-border">
            {task.updates.map((update) => (
              <TaskUpdateItem key={update.id} update={update} />
            ))}
          </div>
        ) : (
          <div className="p-4 text-center">
            <p className="text-xs text-muted-foreground">No updates yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export const ReadTaskBadge = ({ isDone, ...props }: BaseToolBadgeProps) => {
  const allProps = { ...props, isDone };

  const task = useMemo(() => {
    if (!props.toolMessage) return null;
    try {
      if (typeof props.toolMessage.content !== 'string') {
        return null;
      }
      const parsed = JSON.parse(props.toolMessage.content);
      const validated = ReadTaskResponseSchema.safeParse(parsed);

      if (validated.success) {
        return validated.data;
      }
      return null;
    } catch (error) {
      return null;
    }
  }, [props.toolMessage]);

  const baseBadge = (
    <BaseBadge
      {...allProps}
      showArgs={false}
      resultTexts={{
        loading: 'Reading task...',
        done: task
          ? `Read task: ${task.name} (${task.updates.length} update${task.updates.length !== 1 ? 's' : ''})`
          : 'Task loaded',
        error: 'Error reading task',
      }}
    />
  );

  if (!task) {
    return baseBadge;
  }

  return (
    <HoverCard openDelay={0} closeDelay={200}>
      <HoverCardTrigger asChild>
        <div>{baseBadge}</div>
      </HoverCardTrigger>
      <HoverCardContent side="right" align="start" className="w-[550px] p-0">
        <TaskDetails task={task} />
      </HoverCardContent>
    </HoverCard>
  );
};
